import * as React from 'react';
import { useSelector } from 'react-redux';
import { Route, RouteComponentProps, RouteProps, useRouteMatch } from 'react-router-dom';
import { getIdFromAppId, getIdFromOrgId, getIdFromReleaseId } from '../helpers';
import { RootState } from '../reducers/reducers';
import { IntegrationType, ReleaseStepType } from '../types';
import {
    AppRouteComponentMatchParams,
    AppComponentProps,
    IntegrationRouteProps,
    IntegrationsRouteComponentMatchParams,
    OrgRouteComponentMatchParams,
    OrgComponentProps,
    ReleaseRouteComponentMatchParams,
    ReleaseStepRouteComponentMatchParams,
    BucketRouteComponentMatchParams,
    BucketComponentProps,
    ReleaseComponentProps,
    ReleaseStepComponentProps,
    StepRunComponentProps,
    StepRunRouteComponentMatchParams,
    TemporalStepRunComponentProps,
    Nullable,
    StepRunsSummaryRouteComponentMatchParams,
} from '../typescriptHelpers';
import EntityNotFoundComponent from './EntityNotFoundComponent';
import { withRelease } from '@/components/HOCs/withRelease';
import { withReleaseStep } from '@/components/HOCs/withReleaseStep';
import { withStepRunsSummary } from '@/components/HOCs/withStepRunsSummary';

interface ValidatedRouteHOCProps<T extends OrgRouteComponentMatchParams> extends RouteComponentProps<T> {
    showNotFoundComponent?: boolean;
}

interface ValidatedRoutePropsDeprecated extends RouteProps {
    showNotFoundComponent?: boolean;
}

interface ValidatedAppRouteProps {
    children: (validatedAppComponentProps: AppComponentProps) => React.ReactNode;
    showNotFoundComponent?: boolean;
}

interface ValidatedOrgRouteProps {
    children: (validatedOrgComponentProps: OrgComponentProps) => React.ReactNode;
    showNotFoundComponent?: boolean;
}

export const withValidatedOrgRoute = <P extends object>(
    WrappedComponent: React.ComponentType<P & OrgComponentProps>,
) => {
    const ComponentWithValidation: React.FC<P & ValidatedRouteHOCProps<OrgRouteComponentMatchParams>> = (props) => {
        const orgIdParam = props.match?.params?.orgIdParam;

        const orgId = useSelector((state: RootState) => {
            if (orgIdParam) {
                return getIdFromOrgId(orgIdParam, state);
            }
            return null;
        });

        if (orgId) {
            return <WrappedComponent {...props} orgId={orgId} />;
        }

        return props.showNotFoundComponent ? <EntityNotFoundComponent entityType="Org" /> : null;
    };

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ComponentWithValidation.displayName = `withValidatedOrgRoute(${wrappedComponentName})`;

    return ComponentWithValidation;
};

export const withValidatedAppRoute = <P extends object>(
    WrappedComponent: React.ComponentType<P & AppComponentProps>,
) => {
    const ComponentWithValidation: React.FC<P & ValidatedRouteHOCProps<AppRouteComponentMatchParams>> =
        withValidatedOrgRoute((props) => {
            const appIdParam = props.match?.params?.appIdParam;

            const appId = useSelector((state: RootState) => {
                if (props.orgId && appIdParam) {
                    return getIdFromAppId(props.orgId, appIdParam, state);
                }

                return null;
            });

            if (appId) {
                return <WrappedComponent {...props} appId={appId} />;
            }

            return props.showNotFoundComponent ? <EntityNotFoundComponent entityType="App" /> : null;
        });

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ComponentWithValidation.displayName = `withValidatedAppRoute(${wrappedComponentName})`;

    return ComponentWithValidation;
};

export const withValidatedReleaseRoute = <P extends object>(
    WrappedComponent: React.ComponentType<P & ReleaseComponentProps>,
) => {
    const ComponentWithValidation: React.FC<P & ValidatedRouteHOCProps<ReleaseRouteComponentMatchParams>> =
        withValidatedAppRoute((props) => {
            const releaseIdParam = props.match?.params?.releaseIdParam;

            const releaseId = useSelector((state: RootState) => {
                if (props.orgId && props.appId && releaseIdParam) {
                    const releaseIdFromState = getIdFromReleaseId(props.orgId, props.appId, releaseIdParam, state);

                    if (releaseIdFromState) {
                        return releaseIdFromState;
                    } else if (props.appId) {
                        // TODO: Remove!! This is a temporary workaround
                        return `${props.appId}:${releaseIdParam}`;
                    }
                }

                return null;
            });

            if (releaseId) {
                return <WrappedComponent {...props} releaseId={releaseId} />;
            }

            return props.showNotFoundComponent ? <EntityNotFoundComponent entityType="Release" /> : null;
        });

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ComponentWithValidation.displayName = `withValidatedReleaseRoute(${wrappedComponentName})`;

    return ComponentWithValidation;
};

export type ReleaseStepOrStepRunComponentProps = ReleaseStepComponentProps & {
    stepRunComponentProps: Nullable<StepRunComponentProps>;
};

export const withValidatedReleaseStepRoute = <P extends object>(
    WrappedComponent: React.ComponentType<P & ReleaseStepComponentProps & TemporalStepRunComponentProps>,
) => {
    const ComponentWithValidation = withValidatedReleaseRoute<
        P & ValidatedRouteHOCProps<ReleaseStepRouteComponentMatchParams & StepRunRouteComponentMatchParams>
    >((props) => {
        const releaseStepTypeParam = props.match?.params?.releaseStepTypeParam;

        const releaseStepType =
            Object.values(ReleaseStepType).includes(releaseStepTypeParam as ReleaseStepType) ?
                releaseStepTypeParam
            :   null;

        const stepRunId = props.match?.params?.stepRunIdParam ?? releaseStepTypeParam;

        if (releaseStepType) {
            return (
                <WrappedComponent {...props} releaseStepType={releaseStepType as ReleaseStepType} stepRunId={null} />
            );
        } else if (stepRunId) {
            return <WrappedComponent {...props} stepRunId={stepRunId} releaseStepType={null} />;
        }

        return props.showNotFoundComponent ? <EntityNotFoundComponent entityType="Release step" /> : null;
    });

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

    ComponentWithValidation.displayName = `withValidatedReleaseStepRoute(${wrappedComponentName})`;

    return ComponentWithValidation;
};

// TODO: Bring back this code when we are ready to implement the new URL scheme (workflow/workflowRun/stepRun)
// export const withValidatedStepRoute = <P extends object>(
//     WrappedComponent: React.ComponentType<P & StepComponentProps>,
// ) => {
//     const ComponentWithValidation: React.FC<
//         P & ValidatedRouteHOCProps<StepRouteComponentMatchParams, StepComponentProps>
//     > = withValidatedReleaseRoute((props) => {
//         const stepId = props.match?.params?.stepIdParam;
//
//         // TODO: add stepId validation? are we doing uuidMappings for steps?
//
//         if (stepId) {
//             return <WrappedComponent {...props} stepId={stepId} />;
//         }
//
//         return props.showNotFoundComponent ? <EntityNotFoundComponent entityType="Step" /> : null;
//     });
//
//     // Set display name for easier debugging
//     const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
//
//     ComponentWithValidation.displayName = `withValidatedStepRoute(${wrappedComponentName})`;
//
//     return ComponentWithValidation;
// };
//
// export const withValidatedStepRunRoute = <P extends object>(
//     WrappedComponent: React.ComponentType<P & StepRunComponentProps>,
// ) => {
//     const ComponentWithValidation: React.FC<
//         P & ValidatedRouteHOCProps<StepRunRouteComponentMatchParams, StepRunComponentProps>
//     > = withValidatedStepRoute((props) => {
//         const stepRunId = props.match?.params?.stepRunIdParam;
//
//         // TODO: add stepRunId validation? are we doing uuidMappings for stepRuns?
//
//         if (stepRunId) {
//             return <WrappedComponent {...props} stepRunId={stepRunId} />;
//         }
//
//         return props.showNotFoundComponent ? <EntityNotFoundComponent entityType="Step" /> : null;
//     });
//
//     // Set display name for easier debugging
//     const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
//
//     ComponentWithValidation.displayName = `withValidatedStepRunRoute(${wrappedComponentName})`;
//
//     return ComponentWithValidation;
// };
//
// export const ValidatedStepRunRoute: React.FC<ValidatedRoutePropsDeprecated> = (props) => {
//     const { component: Component, path, showNotFoundComponent, ...rest } = props;
//
//     return (
//         <Route
//             {...rest}
//             path={path}
//             render={(routeProps: RouteComponentProps) => {
//                 const ValidatedStepRunComponent = withValidatedStepRunRoute((validatedRouteProps) => {
//                     const { orgId, appId, releaseId, releaseStepType, stepRunId, stepId } = validatedRouteProps;
//
//                     const ComponentWithReleaseStep = withReleaseStep(
//                         Component,
//                         orgId,
//                         appId,
//                         releaseId,
//                         releaseStepType,
//                         stepRunId,
//                         stepId,
//                         true,
//                         true,
//                         false,
//                     );
//                     return <ComponentWithReleaseStep />;
//                 });
//
//                 const stepRunRouteProps = routeProps as RouteComponentProps<StepRunRouteComponentMatchParams>;
//
//                 return (
//                     <ValidatedStepRunComponent {...stepRunRouteProps} showNotFoundComponent={showNotFoundComponent} />
//                 );
//             }}
//         />
//     );
// };

export const withValidatedStepRunsSummaryRoute = <P extends object>(
    WrappedComponent: React.ComponentType<P & StepRunsSummaryComponentProps>,
) => {
    const ComponentWithValidation = withValidatedReleaseRoute<
        P & ValidatedRouteHOCProps<StepRunsSummaryRouteComponentMatchParams>
    >((props) => {
        const groupIndexParam = props.match?.params?.groupIndexParam;
        const archetypeGroupIndexParam = props.match?.params?.archetypeGroupIndexParam;

        const groupIndex = groupIndexParam ? parseInt(groupIndexParam) : null;
        const archetypeGroupIndex = archetypeGroupIndexParam ? parseInt(archetypeGroupIndexParam) : null;

        if (groupIndex !== null && archetypeGroupIndex !== null) {
            return <WrappedComponent {...props} groupIndex={groupIndex} archetypeGroupIndex={archetypeGroupIndex} />;
        }

        return props.showNotFoundComponent ? <EntityNotFoundComponent entityType="Step runs summary" /> : null;
    });

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

    ComponentWithValidation.displayName = `withValidatedReleaseStepRoute(${wrappedComponentName})`;

    return ComponentWithValidation;
};

export interface StepRunsSummaryComponentProps extends ReleaseComponentProps {
    groupIndex: number;
    archetypeGroupIndex: number;
}

export const ValidatedAndLoadedStepRunsSummaryWrapper = withValidatedStepRunsSummaryRoute<{
    children: (props: StepRunsSummaryComponentProps) => React.ReactNode;
    showLoader?: boolean;
    showError?: boolean;
    withTracking?: boolean;
}>(
    withRelease(
        withStepRunsSummary((props) => {
            const { children, ...rest } = props;
            return <>{children(rest)}</>;
        }),
    ),
);

export const ValidatedAndLoadedReleaseStepWrapper = withValidatedReleaseStepRoute<{
    children: (props: ReleaseStepOrStepRunComponentProps) => React.ReactNode;
    showLoader?: boolean;
    showError?: boolean;
    withTracking?: boolean;
}>(
    withRelease(
        withReleaseStep((props) => {
            const { children, ...rest } = props;
            return <>{children(rest)}</>;
        }),
    ),
);

export const ValidatedAndLoadedReleaseWrapper = withValidatedReleaseRoute<{
    children: (props: ReleaseComponentProps) => React.ReactNode;
    showLoader?: boolean;
    showError?: boolean;
    withTracking?: boolean;
}>(
    withRelease((props) => {
        const { children, ...rest } = props;
        return <>{children(rest)}</>;
    }),
);

export const ValidatedAppRouteWrapper: React.FC<ValidatedAppRouteProps> = withValidatedAppRoute((props) => {
    const { children, ...rest } = props;
    return <>{children(rest)}</>;
});

export const ValidatedOrgRouteWrapper: React.FC<ValidatedOrgRouteProps> = withValidatedOrgRoute((props) => {
    const { children, ...rest } = props;
    return <>{children(rest)}</>;
});

// TODO: use withValidatedAppRoute to make it DRY
export const ValidatedIntegrationRedirectRoute: React.FC<ValidatedRoutePropsDeprecated> = (
    props: ValidatedRoutePropsDeprecated,
) => {
    const match = useRouteMatch<IntegrationsRouteComponentMatchParams>(props);

    const { component: Component, path, ...rest } = props;

    const orgIdParam = match?.params?.orgIdParam;
    const appIdParam = match?.params?.appIdParam;
    const integrationTypeParam = match?.params?.integrationTypeParam;

    const orgId = useSelector((state: RootState) => {
        if (orgIdParam) {
            return getIdFromOrgId(orgIdParam, state);
        }
        return null;
    });
    const appId = useSelector((state: RootState) => {
        if (orgIdParam && appIdParam) {
            return getIdFromAppId(orgIdParam, appIdParam, state);
        }
        return null;
    });
    const integrationType = integrationTypeParam as IntegrationType;

    if (orgId && appId && integrationType) {
        const integrationRouteProps: IntegrationRouteProps = {
            orgId,
            appId,
            integrationType,
        };

        return (
            <Route
                {...rest}
                path={path}
                render={(routeProps: RouteComponentProps) => {
                    return <Component {...routeProps} {...integrationRouteProps} />;
                }}
            />
        );
    }

    return null;
};

// TODO: use withValidatedAppRoute to make it DRY
export const ValidatedBucketRoute: React.FC<ValidatedRoutePropsDeprecated> = (props: ValidatedRoutePropsDeprecated) => {
    const match = useRouteMatch<BucketRouteComponentMatchParams>(props);

    const { component: Component, path, ...rest } = props;

    const orgIdParam = match?.params?.orgIdParam;
    const appIdParam = match?.params?.appIdParam;
    const bucketIdParam = match?.params?.bucketIdParam;

    const orgId = useSelector((state: RootState) => {
        if (orgIdParam) {
            return getIdFromOrgId(orgIdParam, state);
        }
        return null;
    });
    const appId = useSelector((state: RootState) => {
        if (orgIdParam && appIdParam) {
            return getIdFromAppId(orgIdParam, appIdParam, state);
        }
        return null;
    });

    if (orgId && appId && bucketIdParam) {
        const bucketRouteProps: BucketComponentProps = {
            orgId,
            appId,
            bucketId: bucketIdParam,
        };

        return (
            <Route
                {...rest}
                path={path}
                render={(routeProps: RouteComponentProps) => {
                    return <Component {...routeProps} {...bucketRouteProps} />;
                }}
            />
        );
    }

    return <EntityNotFoundComponent entityType="Bucket" />;
};
